import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Code, DollarSign, Users, Globe, BookOpen, Award, ArrowRight, CheckCircle, Target, Zap } from 'lucide-react';

const AlgoTradingAcademy = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const staggerChildren = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <div className="min-h-screen bg-black text-white overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center px-4 py-20">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 via-purple-900/20 to-black"></div>

        <motion.div
          className="relative z-10 text-center max-w-6xl mx-auto"
          initial="initial"
          animate="animate"
          variants={staggerChildren}
        >
          <motion.div
            className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-purple-600/20 to-blue-600/20 rounded-full border border-purple-500/30 mb-8"
            variants={fadeInUp}
          >
            <TrendingUp className="w-5 h-5 mr-2 text-purple-400" />
            <span className="text-purple-300 font-medium">The Premier Algo Trading Academy</span>
          </motion.div>

          <motion.h1
            className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-white via-blue-200 to-purple-300 bg-clip-text text-transparent leading-tight"
            variants={fadeInUp}
          >
            Master the Future of
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-400">
              Algorithmic Trading
            </span>
          </motion.h1>

          <motion.p
            className="text-xl md:text-2xl text-gray-300 mb-8 max-w-4xl mx-auto leading-relaxed"
            variants={fadeInUp}
          >
            With <span className="text-purple-400 font-bold">80%</span> of global trading volume now algorithmic,
            Emerging markets remain underrepresented in this <span className="text-blue-400 font-bold">$6 trillion daily market</span>.
            It's time to change that.
          </motion.p>

          <motion.div
            className="flex flex-col sm:flex-row gap-6 justify-center items-center"
            variants={fadeInUp}
          >
            <a
              href="/auth"
              className="group bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 px-8 py-4 rounded-lg font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-xl hover:shadow-purple-500/25 flex items-center"
            >
              Take Free Launchpad
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href="#program-pathway"
              className="border-2 border-purple-500/50 hover:border-purple-400 px-8 py-4 rounded-lg font-bold text-lg transition-all duration-300 hover:bg-purple-500/10 flex items-center"
            >
              See How It Works
              <BookOpen className="ml-2 w-5 h-5" />
            </a>
          </motion.div>

          {/* Stats Row */}
          <motion.div
            className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16 pt-16 border-t border-gray-800"
            variants={staggerChildren}
          >
            <motion.div className="text-center" variants={fadeInUp}>
              <div className="text-4xl font-bold text-purple-400 mb-2">80%</div>
              <div className="text-gray-400">Global Trading Volume</div>
            </motion.div>
            <motion.div className="text-center" variants={fadeInUp}>
              <div className="text-4xl font-bold text-blue-400 mb-2">24/7</div>
              <div className="text-gray-400">Automated Execution</div>
            </motion.div>
            <motion.div className="text-center" variants={fadeInUp}>
              <div className="text-4xl font-bold text-green-400 mb-2">∞</div>
              <div className="text-gray-400">Scalability Potential</div>
            </motion.div>
          </motion.div>
        </motion.div>
      </section>

      {/* Why Algo Trading Section */}
      <section className="py-20 px-4 bg-gradient-to-b from-black to-gray-900/50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
              Why Algorithmic Trading?
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Manual trading is limited by human emotion, fatigue, and subjectivity.
              Algorithms operate with discipline, consistency, and infinite scalability.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Zap,
                title: "Lightning Speed",
                description: "Execute thousands of trades per day automatically",
                color: "text-yellow-400"
              },
              {
                icon: Globe,
                title: "24/7 Operation",
                description: "Operate across multiple markets around the clock",
                color: "text-blue-400"
              },
              {
                icon: Target,
                title: "Emotion-Free",
                description: "Remove human emotion from trading decisions",
                color: "text-purple-400"
              },
              {
                icon: Award,
                title: "Risk Management",
                description: "Apply strict risk and money management rules",
                color: "text-green-400"
              }
            ].map((benefit, index) => (
              <motion.div
                key={index}
                className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 p-6 rounded-xl border border-gray-700/50 hover:border-purple-500/50 transition-all duration-300 transform hover:scale-105"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <benefit.icon className={`w-12 h-12 ${benefit.color} mb-4`} />
                <h3 className="text-xl font-bold mb-2 text-white">{benefit.title}</h3>
                <p className="text-gray-400">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Africa Opportunity Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-purple-900/20 via-blue-900/20 to-purple-900/20">
        <div className="max-w-6xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-gray-800/30 to-gray-900/30 backdrop-blur-sm p-12 rounded-2xl border border-purple-500/20"
          >
            <Globe className="w-16 h-16 text-purple-400 mx-auto mb-8" />
            <h2 className="text-4xl md:text-5xl font-bold mb-8 text-white">
              The Global <span className="text-purple-400">Opportunity</span>
            </h2>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed max-w-4xl mx-auto">
              In mature markets like the U.S. and EU, over 80% of daily trading volume is handled by algorithms.
              This represents a <span className="text-purple-400 font-bold">powerful opportunity</span> for forward-thinking
              individuals in emerging markets to adopt similar models and <span className="text-blue-400 font-bold">leapfrog traditional limitations</span>.
            </p>
            <div className="text-2xl font-bold text-purple-400 mb-4">
              Be Part of the Solution
            </div>
            <p className="text-lg text-gray-400">
              Help bridge the gap. Build the future of algorithmic trading.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Program Pathway Section */}
      <section id="program-pathway" className="py-20 px-4 bg-black">
        <div className="max-w-6xl mx-auto">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
              Your Path to <span className="text-gradient bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">Success</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              From beginner to professional quant - our comprehensive pipeline transforms you into an elite algorithmic trader
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 relative">
            {/* Connection Lines for Desktop */}
            <div className="hidden md:block absolute top-24 left-1/4 right-1/4 h-0.5 bg-gradient-to-r from-purple-500 via-blue-500 to-green-500"></div>

            {[
              {
                step: "01",
                title: "Training & Education",
                description: "Learn Python/MQL5 coding, quantitative finance, and algorithmic trading fundamentals",
                icon: Code,
                color: "purple"
              },
              {
                step: "02",
                title: "Algo Lab Development",
                description: "Build and validate profitable trading systems through rigorous backtesting and optimization",
                icon: BookOpen,
                color: "blue"
              },
              {
                step: "03",
                title: "Live Deployment",
                description: "Connect validated algorithms to live capital from partners and investors",
                icon: TrendingUp,
                color: "green"
              },
              {
                step: "04",
                title: "Profit Sharing",
                description: "Earn performance-based income from successful trading systems you've built",
                icon: DollarSign,
                color: "yellow"
              }
            ].map((step, index) => (
              <motion.div
                key={index}
                className="relative text-center"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
              >
                <div className={`w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-${step.color}-400 to-${step.color}-600 flex items-center justify-center relative z-10`}>
                  <step.icon className="w-8 h-8 text-white" />
                </div>
                <div className={`text-sm font-bold text-${step.color}-400 mb-2`}>{step.step}</div>
                <h3 className="text-xl font-bold mb-3 text-white">{step.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 bg-gradient-to-b from-gray-900/50 to-black">
        <div className="max-w-6xl mx-auto">
          <motion.h2
            className="text-4xl md:text-5xl font-bold text-center mb-16 text-white"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            Impact & Value
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              {[
                {
                  icon: Users,
                  title: "Job Creation",
                  description: "Employment for tech-driven, finance-savvy ambitious youth"
                },
                {
                  icon: DollarSign,
                  title: "Wealth Building",
                  description: "Engineers earn performance-linked income, not just salaries"
                },
                {
                  icon: TrendingUp,
                  title: "Scalability",
                  description: "One system can be deployed across multiple accounts"
                },
                {
                  icon: Award,
                  title: "Credibility",
                  description: "Align with verified, performance-first, data-driven models"
                }
              ].map((benefit, index) => (
                <div key={index} className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <benefit.icon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2 text-white">{benefit.title}</h3>
                    <p className="text-gray-400">{benefit.description}</p>
                  </div>
                </div>
              ))}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-purple-900/30 to-blue-900/30 p-8 rounded-2xl border border-purple-500/20"
            >
              <h3 className="text-2xl font-bold mb-6 text-white">Ready to Start?</h3>
              <div className="space-y-4 mb-8">
                {[
                  "Learn from industry professionals",
                  "Build real trading systems",
                  "Unlock proprietary trading capital",
                  "Performance-based earning potential",
                  "Join the fintech revolution"
                ].map((item, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    <span className="text-gray-300">{item}</span>
                  </div>
                ))}
              </div>
              <a
                href="/auth"
                className="block w-full text-center bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 px-8 py-4 rounded-lg font-bold transition-all duration-300 transform hover:scale-105"
              >
                Apply Now
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-purple-900/40 via-blue-900/40 to-purple-900/40">
        <motion.div
          className="max-w-4xl mx-auto text-center"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-8 text-white">
            Let's Build the Future Together
          </h2>
          <p className="text-xl text-gray-300 mb-10 leading-relaxed">
            This academy represents more than education - it's a movement to reshape trading globally.
            Join us as we develop the next generation of algorithmic traders and financial engineers.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <a
              href="/auth"
              className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 px-10 py-4 rounded-lg font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-2xl"
            >
              Join the Academy
            </a>
            <a
              href="/education"
              className="border-2 border-white/20 hover:border-white/40 px-10 py-4 rounded-lg font-bold text-lg transition-all duration-300 hover:bg-white/5"
            >
              View All Courses
            </a>
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default AlgoTradingAcademy;